import { useContext, useState } from "react";
import { Popover } from "radix-ui";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import classNames from "classnames/bind";
import AppGlobalsContext from "app/AppGlobalsContext";
import { CheckIcon, CopyContentIcon } from "app/icons/navigation";
import { ReleaseTrigger } from "./components/ReleaseTrigger";
import { AbTestPanel } from "./components/AbTestPanel";
import { DevToolsActions } from "./components/DevToolsActions";
import { useDevToolsVisibility } from "./hooks/useDevToolsVisibility";
import { useReleaseVersion } from "./hooks/useReleaseVersion";
import { useAbTests } from "./hooks/useAbTests";
import { useDataLayerViewer } from "./hooks/useDataLayerViewer";
import { useCopyToClipboard } from "./hooks/useCopyToClipboard";
import styles from "./DevToolsPopover.module.scss";
import { RocketIcon } from "./icons";

const cx = classNames.bind(styles);

type AppGlobals = {
	partnerCode?: string;
	strategy?: string;
	envVars?: {
		ENVIRONMENT?: string;
	};
};

type DevToolsPopoverProps = {
	className?: string;
};

/**
 * Floating dev tools popover: release info, A/B tests and debug actions.
 * Renders only on non-production environments.
 */
export const DevToolsPopover = ({ className }: DevToolsPopoverProps) => {
	const isVisible = useDevToolsVisibility();

	if (!isVisible) {
		return null;
	}

	// mounted only when visible, so its hooks (A/B flags, release info…) never run in prod
	return <DevToolsPopoverContent className={className} />;
};

const DevToolsPopoverContent = ({ className }: DevToolsPopoverProps) => {
	const version = useReleaseVersion();
	const { visitorId, variations } = useAbTests();
	const { envVars = {}, partnerCode, strategy } = useContext(AppGlobalsContext) as AppGlobals;
	const environment = envVars.ENVIRONMENT ?? "";
	const [isOpen, setIsOpen] = useState(false);
	const [isVersionHovered, setVersionHovered] = useState(false);
	const prefersReducedMotion = useReducedMotion();
	const { toggleViewer, isViewerOpen } = useDataLayerViewer();
	const { isCopied: isVersionCopied, copy: copyVersion } = useCopyToClipboard();

	const versionGlyph = isVersionCopied ? "check" : isVersionHovered ? "copy" : "rocket";

	return (
		<Popover.Root open={isOpen} onOpenChange={setIsOpen}>
			<Popover.Trigger asChild className={className}>
				<ReleaseTrigger />
			</Popover.Trigger>

			<AnimatePresence>
				{isOpen && (
					<Popover.Portal forceMount>
						<Popover.Content
							className={cx("dev-tools-popover__layer")}
							align="end"
							sideOffset={8}
							collisionPadding={8}
						>
							<motion.div
								className={cx("dev-tools-popover")}
								contentEditable={false}
								style={{
									transformOrigin:
										"var(--radix-popover-content-transform-origin)",
								}}
								initial={
									prefersReducedMotion
										? { opacity: 0 }
										: { opacity: 0, scale: 0.85, y: 6 }
								}
								animate={
									prefersReducedMotion
										? { opacity: 1 }
										: { opacity: 1, scale: 1, y: 0 }
								}
								exit={
									prefersReducedMotion
										? { opacity: 0 }
										: { opacity: 0, scale: 0.85, y: 6 }
								}
								transition={
									prefersReducedMotion
										? { duration: 0.09 }
										: { type: "spring", visualDuration: 0.16, bounce: 0.4 }
								}
							>
								<header
									className={cx("dev-tools-popover__release")}
									data-env={environment}
								>
									<div className={cx("dev-tools-popover__release-top")}>
										<span className={cx("dev-tools-popover__release-caption")}>
											Release
										</span>
										<span className={cx("dev-tools-popover__env")}>
											{environment}
										</span>
									</div>
									<button
										type="button"
										className={cx("dev-tools-popover__version")}
										onClick={() => copyVersion(version)}
										onPointerEnter={() => setVersionHovered(true)}
										onPointerLeave={() => setVersionHovered(false)}
										onFocus={event => {
											if (event.currentTarget.matches(":focus-visible")) {
												setVersionHovered(true);
											}
										}}
										onBlur={() => setVersionHovered(false)}
										title="Copier la version de la release"
										aria-label="Copier la version de la release"
									>
										<span
											className={cx("dev-tools-popover__version-glyph")}
											data-glyph={versionGlyph}
											aria-hidden="true"
										>
											<span
												className={cx(
													"dev-tools-popover__glyph-icon",
													"dev-tools-popover__glyph-icon--rocket"
												)}
											>
												<RocketIcon width={12} height={12} />
											</span>
											<span
												className={cx(
													"dev-tools-popover__glyph-icon",
													"dev-tools-popover__glyph-icon--copy"
												)}
											>
												<CopyContentIcon width={13} height={13} />
											</span>
											<span
												className={cx(
													"dev-tools-popover__glyph-icon",
													"dev-tools-popover__glyph-icon--check"
												)}
											>
												<CheckIcon width={14} height={14} />
											</span>
										</span>
										{version}
										<span
											className={cx("dev-tools-popover__sr-status")}
											aria-live="polite"
										>
											{isVersionCopied ? "Copié" : ""}
										</span>
									</button>
								</header>

								<div className={cx("dev-tools-popover__meta")}>
									<span className={cx("dev-tools-popover__meta-label")}>
										Partenaire / Stratégie
									</span>
									<span className={cx("dev-tools-popover__meta-value")}>
										{partnerCode} · {strategy}
									</span>
								</div>

								<AbTestPanel variations={variations} visitorId={visitorId} />

								<DevToolsActions
									isViewerOpen={isViewerOpen}
									toggleViewer={toggleViewer}
								/>
							</motion.div>
						</Popover.Content>
					</Popover.Portal>
				)}
			</AnimatePresence>
		</Popover.Root>
	);
};
